"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMemo } from "react";

interface PlatformListItemSkeletonProps {
    thumbnail?: boolean;
    title?: boolean;
    subtitle?: boolean;
    options?: boolean;
}

export default function PlatformListItemSkeleton({
    thumbnail = false,
    title = false,
    subtitle = false,
    options = false,
}: PlatformListItemSkeletonProps) {
    const isMobile = useIsMobile();

    const { thumbnailWidth, titleMinWidth, subtitleWidth, optionsWidth } =
        useMemo(
            () => ({
                thumbnailWidth: isMobile ? 150 : 200, // px
                titleMinWidth: isMobile ? 140 : 200, // px
                subtitleWidth: isMobile ? 65 : 160, // px
                optionsWidth: 50, // px
            }),
            [isMobile]
        );

    const gridCols = thumbnail
        ? `${thumbnailWidth}px 1fr ${subtitleWidth}px ${optionsWidth}px`
        : `1fr ${subtitleWidth}px ${optionsWidth}px`;

    return (
        <div
            className="group bg-card hover:bg-card/70 transition grid items-center w-full p-3 gap-2 sm:gap-4 overflow-x-auto animate-pulse"
            style={{ gridTemplateColumns: gridCols }}
        >
            {thumbnail && (
                <div
                    className="aspect-video rounded-md bg-slate-100"
                    style={{ width: thumbnailWidth }}
                />
            )}

            {title && (
                <Skeleton
                    className="h-5 rounded bg-slate-100"
                    style={{ minWidth: titleMinWidth }}
                />
            )}

            {subtitle && (
                <Skeleton
                    className="h-5 rounded bg-slate-100"
                    style={{ width: subtitleWidth }}
                />
            )}

            {options && (
                <div
                    className="flex justify-end"
                    style={{ width: optionsWidth }}
                >
                    <Skeleton className="h-5 w-full rounded bg-slate-100" />
                </div>
            )}
        </div>
    );
}
